import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { getformInputList, saveAllFieldsApi } from "../services/dynamicForms";
import { toast } from "react-hot-toast";
import CustomToaster from "../components/Toaster";
import { TOAST_TYPE, TOAST_TIME, TOAST_TITLE } from "../constants/constants";


export const saveAllFields = createAsyncThunk(
  "formBuilder/saveAllFields",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await saveAllFieldsApi(payload);

      toast.custom(
        <CustomToaster
          type={TOAST_TYPE.SUCCESS}
          title={TOAST_TITLE.SUCCESS}
          message="Form fields saved successfully."
          time={TOAST_TIME.FIVE}
        />
      );

      return response.data;
    } catch (error) {
      toast.custom(
        <CustomToaster
          type={TOAST_TYPE.ERROR}
          message="Failed to save form fields."
          time={TOAST_TIME.FIVE}
        />
      );
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const getFormInputList = createAsyncThunk(
  "formBuilder/getFormInputList",
  async (formId, { rejectWithValue }) => {
    try {
      const response = await getformInputList(formId);
      return response.data;
    } catch (error) {
      toast.custom(
        <CustomToaster
          type={TOAST_TYPE.ERROR}
          message="Failed to fetch form inputs."
          time={TOAST_TIME.FIVE}
        />
      );

      return rejectWithValue(error.response?.data || error.message);
    }
  }
);



const formBuilderSlice = createSlice({
  name: "formBuilder",
  initialState: {
    fields: [],
    deleteIds: [],
    loading: false,
    error: null,
  },

  reducers: {
    setFields(state, action) {
      state.fields = action.payload || [];
    },

    addField(state, action) {
      state.fields.push(action.payload);
    },

    updateField(state, action) {
      const { id, data } = action.payload;
      const index = state.fields.findIndex((f) => f.id === id || f.tempId === id);
      if (index !== -1) state.fields[index] = { ...state.fields[index], ...data };
    },

    deleteField(state, action) {
      const idToDelete = action.payload;

      state.fields = state.fields.filter(
        (f) => f.id !== idToDelete && f.tempId !== idToDelete
      );

      if (typeof idToDelete === "number") {
        state.deleteIds.push(idToDelete);
      }
    },

    moveFieldUp(state, action) {
      const idx = state.fields.findIndex((f) => f.id === action.payload || f.tempId === action.payload);
      if (idx > 0) {
        const temp = state.fields[idx - 1];
        state.fields[idx - 1] = state.fields[idx];
        state.fields[idx] = temp;
      }
    },

    moveFieldDown(state, action) {
      const idx = state.fields.findIndex((f) => f.id === action.payload || f.tempId === action.payload);
      if (idx < state.fields.length - 1) {
        const temp = state.fields[idx + 1];
        state.fields[idx + 1] = state.fields[idx];
        state.fields[idx] = temp;
      }
    },

    clearFields(state) {
      state.fields = [];
      state.deleteIds = [];
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(saveAllFields.pending, (state) => {
        state.loading = true;
        state.error = null;
      })

      .addCase(saveAllFields.fulfilled, (state) => {
        state.loading = false;
      })

      .addCase(saveAllFields.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(getFormInputList.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getFormInputList.fulfilled, (state, action) => {
        state.loading = false;
        const normalized = (action.payload?.results || []).map(field => ({
          ...field,
          options: Array.isArray(field.options?.results) ? field.options?.results : [],
        }));

        state.fields = normalized;

      })
      .addCase(getFormInputList.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const {
  setFields,
  addField,
  updateField,
  deleteField,
  moveFieldUp,
  moveFieldDown,
  clearFields
} = formBuilderSlice.actions;

export default formBuilderSlice.reducer;


